<script setup>
const props = defineProps({
    searches: { type: Array, default: () => [] },
    selectedId: { type: Number, default: null },
    showingArchived: { type: Boolean, default: false },
    hasLeadHunterCampaigns: { type: Boolean, default: false },
});

const emit = defineEmits([
    'archive',
    'refresh',
    'select',
    'toggle-archived',
    'unarchive',
]);
</script>

<template>
    <div class="h-full flex flex-col">
        <div class="p-3 border-b border-gray-200">
            <h3 class="text-xs font-semibold text-gray-900 mb-0">
                <mf-translate
                    domainkey="lead-hunter:scout.savedSearches"
                ></mf-translate>
            </h3>
        </div>

        <div class="flex-1 overflow-y-auto">
            <div v-if="searches.length === 0" class="p-3">
                <div class="text-center py-4">
                    <div
                        class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2"
                    >
                        <i
                            class="fa-regular fa-magnifying-glass text-gray-400 text-xs"
                        ></i>
                    </div>
                    <p class="text-[11px] text-gray-500 mb-1">
                        {{ _mft('lead-hunter:scout.noSearchesTitle') }}
                    </p>
                    <p class="text-[10px] text-gray-400 mb-0">
                        {{ _mft('lead-hunter:scout.noSearchesHint') }}
                    </p>
                </div>
            </div>

            <div
                v-for="search in searches"
                :key="search.id"
                class="group/item px-3 py-2.5 border-b border-gray-100 cursor-pointer transition-colors"
                :class="
                    selectedId === search.id
                        ? 'bg-myflowLavender-light text-myflowPurple-900'
                        : 'hover:bg-gray-50'
                "
                @click="emit('select', search)"
            >
                <div class="flex items-start gap-2 relative">
                    <div class="min-w-0 flex-1">
                        <p
                            class="text-xs font-medium text-gray-900 truncate mb-0"
                        >
                            {{ search.name }}
                        </p>
                        <p class="text-[10px] text-gray-500 mt-0.5 mb-0">
                            {{ search.contacted_count }}
                            {{ _mft('lead-hunter:scout.leadsCollected') }}
                        </p>
                    </div>
                    <div class="absolute right-0 top-0 bottom-0">
                        <pill
                            v-if="search.has_pending_results"
                            size="xs"
                            style-type="success"
                            class="opacity-0 group-hover/item:opacity-100"
                        >
                            {{ _mft('shared:active') }}
                        </pill>
                        <container-with-tooltip
                            v-if="!search.has_pending_results"
                            additional-tooltip-class=""
                            alignment="right"
                        >
                            <button
                                type="button"
                                class="h-7 w-7 rounded-md text-gray-400 opacity-0 bg-gray-50 hover:bg-gray-100 hover:text-gray-700 group-hover/item:opacity-100 focus:opacity-100"
                                :aria-label="
                                    showingArchived
                                        ? _mft(
                                              'lead-hunter:scout.unarchiveSearch'
                                          )
                                        : _mft(
                                              'lead-hunter:scout.archiveSearch'
                                          )
                                "
                                @click.stop="
                                    emit(
                                        showingArchived
                                            ? 'unarchive'
                                            : 'archive',
                                        search
                                    )
                                "
                            >
                                <i
                                    :class="
                                        showingArchived
                                            ? 'fa-regular fa-box-open'
                                            : 'fa-regular fa-box-archive'
                                    "
                                ></i>
                            </button>

                            <template #tooltip>
                                {{
                                    showingArchived
                                        ? _mft(
                                              'lead-hunter:scout.unarchiveSearch'
                                          )
                                        : _mft(
                                              'lead-hunter:scout.archiveSearch'
                                          )
                                }}
                            </template>
                        </container-with-tooltip>
                    </div>
                </div>
            </div>

            <div class="p-3">
                <button
                    type="button"
                    class="w-full flex items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    @click="emit('toggle-archived')"
                >
                    <i
                        :class="
                            showingArchived
                                ? 'fa-regular fa-list'
                                : 'fa-regular fa-box-archive'
                        "
                    ></i>
                    <span>
                        {{
                            showingArchived
                                ? _mft('lead-hunter:scout.showActiveSearches')
                                : _mft('lead-hunter:scout.showArchivedSearches')
                        }}
                    </span>
                </button>

                <a
                    v-if="hasLeadHunterCampaigns"
                    :href="route('assistant.daily')"
                    class="mt-2 w-full flex items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                >
                    <i class="fa-regular fa-calendar-check"></i>
                    <span>{{ _mft('navigation:assistant.daily') }}</span>
                    <i class="fa-regular fa-chevron-right"></i>
                </a>
            </div>
        </div>
    </div>
</template>
